/**
 * Auth0 PKCE, via the official SPA SDK.
 *
 * Uses the SDK rather than hand-rolling the flow: the auth plan's razor is "no custom
 * crypto, no hand-rolled token formats — standard OIDC/JWT". PKCE has enough sharp
 * edges (verifier storage, state validation, replay) that reimplementing it to save a
 * dependency is a poor trade.
 */
import { Auth0Client, createAuth0Client, User } from "@auth0/auth0-spa-js";
import { cfg } from "./config";

let client: Auth0Client | null = null;

export async function auth(): Promise<Auth0Client> {
  if (client) return client;
  client = await createAuth0Client({
    domain: cfg.auth0Domain,
    clientId: cfg.auth0ClientId,
    authorizationParams: {
      redirect_uri: window.location.origin,
      // Without this the access token is OPAQUE, not a JWT — see config.ts.
      audience: cfg.audience,
    },
    // Refresh tokens + rotation, so a session survives a reload and can be renewed
    // without a full redirect. localStorage rather than memory for the same reason:
    // reloading the page should not log you out.
    useRefreshTokens: true,
    cacheLocation: "localstorage",
  });
  return client;
}

/**
 * An error handed back by Auth0 on the redirect, if any.
 *
 * Auth0 signals a REFUSED login by redirecting to `?error=...&error_description=...`
 * with no `code`. Our own post-login Action refuses this way whenever the access service
 * is unreachable or answers non-200 — so this is not an exotic case, it is the failure
 * mode of a component we operate. Ignoring it produces a login screen that appears to
 * shrug off a successful Google sign-in.
 */
export function redirectError(): { error: string; description: string } | null {
  const p = new URLSearchParams(window.location.search);
  const error = p.get("error");
  if (!error) return null;
  return { error, description: p.get("error_description") ?? "" };
}

/** Remove callback params so a reload is not a second, now-invalid, exchange. */
export function clearCallbackParams(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}

/** Complete a redirect callback, if this load is one. */
export async function handleRedirect(): Promise<void> {
  const q = window.location.search;
  if (!q.includes("code=") || !q.includes("state=")) return;
  const c = await auth();
  await c.handleRedirectCallback();
  // Strip the callback params so a reload is not a second, now-invalid, exchange.
  window.history.replaceState({}, document.title, window.location.pathname);
}

export async function isLoggedIn(): Promise<boolean> {
  return (await auth()).isAuthenticated();
}

export async function currentUser(): Promise<User | undefined> {
  return (await auth()).getUser();
}

export async function login(): Promise<void> {
  const c = await auth();
  await c.loginWithRedirect({
    // Straight to Google — the tenant has one connection and an interstitial account
    // picker adds a click with nothing behind it.
    authorizationParams: { connection: "google-oauth2" },
  });
}

export async function logout(): Promise<void> {
  const c = await auth();
  await c.logout({ logoutParams: { returnTo: window.location.origin } });
}

export type TokenResult = { token: string } | { error: string };

/**
 * A usable access token, or the REASON one could not be obtained.
 *
 * The reason is returned rather than swallowed. A bare `catch { return null }` here cost
 * real debugging time: every distinct failure — no refresh token, a genuinely expired
 * session, a blocked iframe — collapsed into the same silent null, and the app treated
 * all of them as "log out".
 */
export async function getAccessToken(): Promise<TokenResult> {
  try {
    const c = await auth();
    const token = await c.getTokenSilently({
      authorizationParams: { audience: cfg.audience },
    });
    // auth0-spa-js types this as `string | undefined`: with `detailedResponse` it hands
    // back an object instead, so the plain-string overload cannot promise one. It should
    // never be undefined on this path, and treating "should never" as "will never" is
    // how a silent empty password reaches the broker — which fails as an auth error
    // pointing at the token, not at the code that forgot to check.
    if (!token) return { error: "auth0 returned no access token" };
    return { token };
  } catch (e) {
    // auth0-spa-js uses `error` for its machine-readable code (missing_refresh_token,
    // login_required, consent_required); the message alone does not distinguish them.
    const err = e as { error?: string; message?: string };
    console.error("[fleet] getTokenSilently failed:", e);
    return { error: err.error ?? err.message ?? String(e) };
  }
}

/** Convenience for callers that only need the value (the ACL banner). */
export async function accessToken(): Promise<string | null> {
  const r = await getAccessToken();
  return "token" in r ? r.token : null;
}

/**
 * The `sub` claim, used as the MQTT username.
 *
 * The broker ignores the username for authentication (the token in the password field
 * is what it validates) but logs it, so sending the subject makes broker logs legible.
 */
export async function subject(): Promise<string> {
  return (await currentUser())?.sub ?? "fleet-web";
}

/**
 * The topic filters this token is allowed to SUBSCRIBE to.
 *
 * NOT client-side filtering, and not a security check — it is asking for exactly what we
 * may have. MQTT authorization does not narrow an over-broad subscription, it REFUSES
 * it: a token granted `sites/acme/robots/+/#` that asks for `sites/+/robots/+/#` gets
 * SUBACK 128 and receives nothing at all. Measured, and confirmed in the broker log as
 * `authorization_permission_denied ... action: SUBSCRIBE`.
 *
 * So the app must request its own grants. The broker still enforces them; this only
 * stops us asking for more than we hold and being refused everything as a result.
 */
export function subscribeFiltersFromToken(token: string): string[] {
  const acl = aclFromToken(token);
  if (!acl) return [];
  const out: string[] = [];
  for (const rule of acl as Array<Record<string, unknown>>) {
    if (rule?.permission !== "allow") continue;
    const action = String(rule?.action ?? "");
    // "all" covers both directions in EMQX's rule vocabulary.
    if (action !== "subscribe" && action !== "all") continue;
    const topic = rule?.topic;
    if (typeof topic === "string" && topic) out.push(topic);
  }
  return [...new Set(out)];
}

/**
 * Decode the ACL claim for display. NOT a security check — the browser's opinion of a
 * token is worthless, and the broker re-validates everything. This exists so a user with
 * no grants sees "you have no access" instead of an empty screen that looks broken.
 */
export function aclFromToken(token: string): unknown[] | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    const acl = payload["https://norbot.io/acl"];
    return Array.isArray(acl) ? acl : null;
  } catch {
    return null;
  }
}


/**
 * Every site this token can see, from the ACL claim.
 *
 * THE AUTHORITATIVE LIST, and better than anything observed: a site appears here whether
 * or not its adapter has published a roster, a manifest, or anything at all. Deriving
 * the list from traffic instead would hide exactly the site you most need to look at —
 * the quiet one — and that is the bug this replaced. It also arrives before the broker
 * connection does, so the first screen has content immediately.
 *
 * Not a security check. The broker re-validates every subscribe; this only decides what
 * to put on screen.
 */
export function sitesFromToken(token: string): string[] {
  const out = new Set<string>();
  for (const rule of (aclFromToken(token) ?? []) as Array<Record<string, unknown>>) {
    if (rule?.permission !== "allow") continue;
    const topic = String(rule?.topic ?? "");
    // `sites/{siteId}/...`, which is the cloud deployment root — see the contract's
    // "Deployment roots". A wildcard site is not a site and must not become one.
    const m = /^sites\/([^/+#]+)\//.exec(topic);
    if (m) out.add(m[1]);
  }
  return [...out].sort();
}
